import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe, getOrganizations as fetchOrgs, sendHeartbeat, getMyNotifications, markAllNotificationsRead, markNotificationRead } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pinnedOrgIds, setPinnedOrgIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pinnedOrgIds') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getMe()
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const refreshOrgs = useCallback(async () => {
    try {
      const res = await fetchOrgs();
      setOrganizations(res.data);
    } catch (err) {
      console.error('Failed to fetch orgs', err);
    }
  }, []);

  useEffect(() => {
    if (user) refreshOrgs();
  }, [user, refreshOrgs]);

  // Heartbeat for activity tracking
  useEffect(() => {
    if (!user) return;
    sendHeartbeat().catch(() => {});
    const interval = setInterval(() => {
      sendHeartbeat().catch(() => {});
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // Notification polling
  const refreshNotifications = useCallback(async () => {
    try {
      const res = await getMyNotifications();
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshNotifications();
    const interval = setInterval(refreshNotifications, 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      refreshNotifications();
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      refreshNotifications();
    } catch (err) {
      console.error('Failed to mark all read', err);
    }
  };

  const togglePinOrg = (orgId) => {
    setPinnedOrgIds(prev => {
      const next = prev.includes(orgId) ? prev.filter(id => id !== orgId) : [...prev, orgId];
      localStorage.setItem('pinnedOrgIds', JSON.stringify(next));
      return next;
    });
  };

  const loginUser = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // restore pinned orgs from localStorage so pins persist across relogin
    try {
      const stored = JSON.parse(localStorage.getItem('pinnedOrgIds') || '[]');
      setPinnedOrgIds(stored);
    } catch {
      setPinnedOrgIds([]);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setOrganizations([]);
    setNotifications([]);
    setUnreadCount(0);
    // keep pinnedOrgIds in localStorage so pins survive re-login in the same browser
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logout, organizations, refreshOrgs, notifications, unreadCount, refreshNotifications, handleMarkRead, handleMarkAllRead, pinnedOrgIds, togglePinOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
